import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  SparklesIcon,
  CheckIcon,
  CursorArrowRaysIcon,
  CodeBracketIcon,
  LightBulbIcon,
  ChartBarIcon,
  ChartPieIcon,
  TableCellsIcon,
  PresentationChartLineIcon,
  ArrowTrendingUpIcon,
  Squares2X2Icon,
  GlobeAltIcon,
  CircleStackIcon,
  RectangleStackIcon,
  TableCellsIcon as TableIcon,
} from '@heroicons/react/24/outline';
import { apiHelpers } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useChartBuilderStore } from '../../store/chartBuilderStore';
import MultiSourceSelector from '../common/MultiSourceSelector';
import AIAssistantTab from '../chart-builder/AIAssistantTab';
import VisualBuilderTab from '../chart-builder/VisualBuilderTab';
import SqlEditorTab from '../chart-builder/SqlEditorTab';
import ChartPreview from '../chart-builder/ChartPreview';

const CHART_TYPES = [
  { id: 'bar', name: 'Bar', icon: ChartBarIcon, description: 'Compare categories' },
  { id: 'line', name: 'Line', icon: PresentationChartLineIcon, description: 'Show trends' },
  { id: 'pie', name: 'Pie', icon: ChartPieIcon, description: 'Show proportions' },
  { id: 'donut', name: 'Donut', icon: ChartPieIcon, description: 'Ring proportions' },
  { id: 'area', name: 'Area', icon: ArrowTrendingUpIcon, description: 'Volume over time' },
  { id: 'scatter', name: 'Scatter', icon: CursorArrowRaysIcon, description: 'Data correlation' },
  { id: 'funnel', name: 'Funnel', icon: ArrowTrendingUpIcon, description: 'Conversion stages' },
  { id: 'heatmap', name: 'Heatmap', icon: Squares2X2Icon, description: 'Density patterns' },
  { id: 'cohort_table', name: 'Cohort', icon: Squares2X2Icon, description: 'Retention matrix' },
  { id: 'geo', name: 'Geo Map', icon: GlobeAltIcon, description: 'Location mapping' },
  { id: 'table', name: 'Table', icon: TableCellsIcon, description: 'Detailed data' },
  { id: 'kpi', name: 'KPI', icon: CircleStackIcon, description: 'Single metric' },
];

export default function UnifiedChartModal({ isOpen, onClose, dashboardId, onChartCreated }) {
  const { getCurrentCompanyId, isSuperUser } = useAuthStore();

  const [activeTab, setActiveTab] = useState('ai');
  const [mobileTab, setMobileTab] = useState('preview');
  const [noCompanySelected, setNoCompanySelected] = useState(false);

  const {
    chartType,
    recommendedChartType,
    sourceMode,
    selectedDataSource,
    selectedDataSources,
    selectedSourceType,
    aiSelectedSource,
    aiSelectedSources,
    dataSources,
    wideViews,
    relationships,
    setChartTypeWithConfig,
    setSourceConfig,
    setDataSources,
    setSchema,
    reset,
  } = useChartBuilderStore();

  useEffect(() => {
    if (isOpen) {
      fetchChartSources();
      reset();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && selectedDataSource && selectedDataSource !== 'auto') {
      fetchSchema();
    } else if (selectedDataSource === 'auto' || !selectedDataSource) {
      setSchema(null, '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedDataSource, selectedSourceType]);

  const fetchChartSources = async () => {
    try {
      const companyId = getCurrentCompanyId();
      if (!companyId && !isSuperUser()) {
        setNoCompanySelected(true);
        setDataSources([], [], [], []);
        return;
      }
      if (!companyId && isSuperUser()) {
        setNoCompanySelected(true);
        setDataSources([], [], [], []);
        return;
      }
      setNoCompanySelected(false);

      const [sourcesRes, relRes] = await Promise.all([
        apiHelpers.getChartSources({ companyId }),
        apiHelpers.getRelationships({ companyId }).catch(() => ({ data: { relationships: [] } })),
      ]);

      const dataSourcesData = sourcesRes.data.dataSources || [];
      const wideViewsData = sourcesRes.data.wideViews || sourcesRes.data.views || [];
      const martsData = sourcesRes.data.marts || [];
      const rels = relRes.data?.relationships || relRes.data || [];

      setDataSources(dataSourcesData, wideViewsData, martsData, rels);
    } catch (error) {
      console.error('Failed to fetch chart sources:', error);
    }
  };

  const fetchSchema = async () => {
    try {
      const state = useChartBuilderStore.getState();
      const wv = state.wideViews;
      const m = state.marts;

      if (selectedSourceType === 'mart') {
        const mart = m.find((mart) => mart._id === selectedDataSource);
        if (mart) {
          setSchema(mart.schemaInfo, mart.dbtModelName || `mart_${selectedDataSource.replace('mart_', '')}`);
        }
      } else if (selectedSourceType === 'wideview' || selectedSourceType === 'view') {
        const w = wv.find((wvItem) => wvItem._id === selectedDataSource);
        if (w) {
          const viewTableName = w.bigQueryViewName || `view_${(w.name || selectedDataSource).toLowerCase().replace(/\s+/g, '_').replace(/\(view\)/i, '').trim()}`;
          const cachedSchema = w.schemaInfo;
          const hasRealColumns = cachedSchema?.tables?.[0]?.columns?.some((c) => c.name !== '*');

          if (hasRealColumns) {
            setSchema(cachedSchema, w.dbtModelName || viewTableName);
          } else {
            try {
              const companyId = getCurrentCompanyId();
              const bqRes = await apiHelpers.getBigQueryTableSchema({
                tableName: viewTableName,
                companyId,
              });
              if (bqRes.data?.success && bqRes.data.columns?.length > 0) {
                setSchema(
                  { tables: [{ columns: bqRes.data.columns }] },
                  w.dbtModelName || viewTableName,
                );
              } else {
                setSchema(cachedSchema, w.dbtModelName || viewTableName);
              }
            } catch {
              setSchema(cachedSchema, w.dbtModelName || viewTableName);
            }
          }
        }
      } else {
        const response = await apiHelpers.getDataSourceSchema(selectedDataSource);
        setSchema(response.data.schema, response.data.bigQueryTable);
      }
    } catch (error) {
      console.error('Failed to fetch schema:', error);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    if (tab !== 'ai' && sourceMode === 'auto') {
      setSourceConfig({
        sourceMode: 'single',
        selectedDataSource: '',
        selectedDataSources: [],
      });
    }
  };

  const resolveSourceType = (id) => {
    const state = useChartBuilderStore.getState();
    if (state.wideViews.some((v) => v._id === id)) return 'wideview';
    if (state.marts.some((m) => m._id === id)) return 'mart';
    return 'datasource';
  };

  const handleSourceChange = (sources) => {
    if (sourceMode === 'multi') {
      setSourceConfig({ selectedDataSources: sources });
      if (sources.length > 0 && sources[0] !== selectedDataSource) {
        const id = sources[0];
        setSourceConfig({
          selectedDataSource: id,
          selectedSourceType: resolveSourceType(id),
        });
      } else if (sources.length === 0) {
        setSourceConfig({ selectedDataSource: '', selectedSourceType: 'datasource' });
      }
    } else {
      const id = sources[0] || '';
      setSourceConfig({ selectedDataSource: id });
      if (id) {
        setSourceConfig({ selectedSourceType: resolveSourceType(id) });
      }
    }
  };

  const views = wideViews;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden safe-modal-height">
          <div className="flex h-full items-stretch justify-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-7xl transform overflow-hidden bg-white dark:bg-surface-900 shadow-2xl transition-all flex flex-col h-full">
                <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-surface-200 dark:border-surface-800 bg-gradient-to-r from-primary-600 to-accent-600">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                      <SparklesIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <div className="min-w-0">
                      <Dialog.Title className="text-base sm:text-lg font-display font-semibold text-white truncate">
                        Add Chart
                      </Dialog.Title>
                      <p className="text-xs sm:text-sm text-white/70 hidden sm:block">
                        Use AI, Visual Builder, or SQL to create charts
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleClose}
                    className="p-2 rounded-lg hover:bg-white/10 transition-colors flex-shrink-0"
                  >
                    <XMarkIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </button>
                </div>

                <div className="flex lg:hidden border-b border-surface-200 dark:border-surface-800">
                  <button
                    onClick={() => setMobileTab('preview')}
                    className={`flex-1 px-4 py-2.5 text-sm font-medium text-center transition-colors ${
                      mobileTab === 'preview'
                        ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-500 bg-primary-50/50 dark:bg-primary-900/20'
                        : 'text-surface-500 hover:text-surface-700 dark:hover:text-surface-300'
                    }`}
                  >
                    Preview
                  </button>
                  <button
                    onClick={() => setMobileTab('controls')}
                    className={`flex-1 px-4 py-2.5 text-sm font-medium text-center transition-colors ${
                      mobileTab === 'controls'
                        ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-500 bg-primary-50/50 dark:bg-primary-900/20'
                        : 'text-surface-500 hover:text-surface-700 dark:hover:text-surface-300'
                    }`}
                  >
                    Builder
                  </button>
                </div>

                <div className="flex-1 flex flex-col lg:flex-row overflow-hidden min-h-0">
                  {/* Left panel: Chart Type + Live Preview */}
                  <div
                    className={`${mobileTab === 'preview' ? 'flex' : 'hidden'} lg:flex w-full lg:w-1/2 flex-1 flex-col min-h-0 overflow-hidden border-b lg:border-b-0 lg:border-r border-surface-200 dark:border-surface-800`}
                  >
                    <div className="p-3 sm:p-4 border-b border-surface-200 dark:border-surface-800 bg-surface-50 dark:bg-surface-800/50">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-surface-700 dark:text-surface-300">
                          Chart Type
                        </label>
                        {recommendedChartType && (
                          <div className="flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400">
                            <LightBulbIcon className="w-4 h-4" />
                            AI recommends: <span className="font-medium capitalize">{recommendedChartType}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1.5 sm:gap-2 flex-wrap">
                        {CHART_TYPES.map((type) => {
                          const Icon = type.icon;
                          const isRecommended = type.id === recommendedChartType;
                          const isSelected = type.id === chartType;
                          return (
                            <button
                              key={type.id}
                              onClick={() => setChartTypeWithConfig(type.id)}
                              className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border-2 transition-all text-xs sm:text-sm ${
                                isSelected
                                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                                  : isRecommended
                                    ? 'border-primary-300 bg-primary-25 dark:bg-primary-900/10 text-surface-700 dark:text-surface-300'
                                    : 'border-surface-200 dark:border-surface-700 text-surface-600 dark:text-surface-400 hover:border-surface-400'
                              }`}
                              title={type.description}
                            >
                              <Icon className="w-4 h-4" />
                              {type.name}
                              {isRecommended && !isSelected && (
                                <span className="w-2 h-2 rounded-full bg-primary-500" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <ChartPreview
                      dashboardId={dashboardId}
                      onChartCreated={(chart) => {
                        onChartCreated?.(chart);
                        handleClose();
                      }}
                    />
                  </div>

                  {/* Right panel: Data Source + Tabs (AI / Visual / SQL) */}
                  <div
                    className={`${mobileTab === 'controls' ? 'flex' : 'hidden'} lg:flex w-full lg:w-1/2 flex-1 flex-col min-h-0 overflow-hidden`}
                  >
                    <div className="flex-shrink-0 p-4 border-b border-surface-200 dark:border-surface-800 bg-surface-50 dark:bg-surface-800/50">
                      <div className="flex items-center justify-between mb-2 gap-2">
                        <label className="text-sm font-medium text-surface-700 dark:text-surface-300">
                          Data Source
                        </label>
                        <div className="flex items-center gap-1 bg-surface-100 dark:bg-surface-800 rounded-lg p-0.5">
                          <button
                            onClick={() =>
                              setSourceConfig({
                                sourceMode: 'auto',
                                selectedDataSource: 'auto',
                                selectedDataSources: [],
                              })
                            }
                            disabled={activeTab !== 'ai'}
                            className={`px-2 py-1 text-xs rounded transition-colors ${
                              sourceMode === 'auto'
                                ? 'bg-primary-500 text-white'
                                : activeTab !== 'ai'
                                  ? 'text-surface-400 dark:text-surface-600 cursor-not-allowed opacity-50'
                                  : 'text-surface-600 dark:text-surface-400 hover:bg-surface-200'
                            }`}
                            title={activeTab !== 'ai' ? 'Auto mode is only available with AI Assistant' : ''}
                          >
                            Auto
                          </button>
                          <button
                            onClick={() =>
                              setSourceConfig({
                                sourceMode: 'single',
                                selectedDataSource: '',
                                selectedDataSources: [],
                              })
                            }
                            className={`px-2 py-1 text-xs rounded transition-colors ${
                              sourceMode === 'single'
                                ? 'bg-primary-500 text-white'
                                : 'text-surface-600 dark:text-surface-400 hover:bg-surface-200'
                            }`}
                          >
                            <TableIcon className="w-3.5 h-3.5 inline mr-1" />
                            Single
                          </button>
                          <button
                            onClick={() =>
                              setSourceConfig({
                                sourceMode: 'multi',
                                selectedDataSource: '',
                                selectedDataSources: [],
                              })
                            }
                            className={`px-2 py-1 text-xs rounded transition-colors ${
                              sourceMode === 'multi'
                                ? 'bg-primary-500 text-white'
                                : 'text-surface-600 dark:text-surface-400 hover:bg-surface-200'
                            }`}
                          >
                            <RectangleStackIcon className="w-3.5 h-3.5 inline mr-1" />
                            Multi
                          </button>
                        </div>
                      </div>

                      {noCompanySelected ? (
                        <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                          <p className="text-sm text-amber-800 dark:text-amber-200">
                            Please select a company first.
                          </p>
                        </div>
                      ) : sourceMode === 'auto' ? (
                        <div className="p-3 bg-gradient-to-r from-primary-50 to-purple-50 dark:from-primary-900/20 dark:to-purple-900/20 rounded-lg border border-primary-200 dark:border-primary-800">
                          <div className="flex items-center gap-2">
                            <SparklesIcon className="w-5 h-5 text-primary-500" />
                            <div>
                              <p className="text-sm font-medium text-primary-700 dark:text-primary-300">
                                AI Auto-Select
                              </p>
                              <p className="text-xs text-primary-600 dark:text-primary-400">
                                AI will pick the best source(s). Use in AI Assistant tab.
                              </p>
                            </div>
                          </div>
                          {(aiSelectedSource || (aiSelectedSources && aiSelectedSources.length > 0)) && (
                            <div className="mt-2 pt-2 border-t border-primary-200 dark:border-primary-700">
                              <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                                <CheckIcon className="w-3 h-3" />
                                AI selected:{' '}
                                <span className="font-medium">
                                  {aiSelectedSources?.length > 1
                                    ? `${aiSelectedSources.length} sources`
                                    : aiSelectedSource?.name || aiSelectedSources?.[0]?.name}
                                </span>
                              </p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <MultiSourceSelector
                          dataSources={dataSources}
                          views={views}
                          relationships={relationships}
                          selected={
                            sourceMode === 'multi'
                              ? selectedDataSources
                              : selectedDataSource
                                ? [selectedDataSource]
                                : []
                          }
                          onChange={handleSourceChange}
                          mode={sourceMode}
                          showRelationships={sourceMode === 'multi'}
                        />
                      )}
                    </div>

                    <div className="flex-shrink-0 flex border-b border-surface-200 dark:border-surface-800">
                      <button
                        onClick={() => setActiveTab('ai')}
                        className={`flex-1 px-4 py-2.5 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                          activeTab === 'ai'
                            ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-500 bg-primary-50/50 dark:bg-primary-900/20'
                            : 'text-surface-500 hover:text-surface-700 dark:hover:text-surface-300'
                        }`}
                      >
                        <SparklesIcon className="w-4 h-4" />
                        AI Assistant
                      </button>
                      <button
                        onClick={() => handleTabSwitch('visual')}
                        className={`flex-1 px-4 py-2.5 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                          activeTab === 'visual'
                            ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-500 bg-primary-50/50 dark:bg-primary-900/20'
                            : 'text-surface-500 hover:text-surface-700 dark:hover:text-surface-300'
                        }`}
                      >
                        <CursorArrowRaysIcon className="w-4 h-4" />
                        Visual Builder
                      </button>
                      <button
                        onClick={() => handleTabSwitch('sql')}
                        className={`flex-1 px-4 py-2.5 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                          activeTab === 'sql'
                            ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-500 bg-primary-50/50 dark:bg-primary-900/20'
                            : 'text-surface-500 hover:text-surface-700 dark:hover:text-surface-300'
                        }`}
                      >
                        <CodeBracketIcon className="w-4 h-4" />
                        SQL Editor
                      </button>
                    </div>

                    <div className="flex-1 min-h-0 overflow-hidden">
                      {activeTab === 'ai' && <AIAssistantTab />}
                      {activeTab === 'visual' && (
                        <div className="h-full overflow-y-auto p-4">
                          <VisualBuilderTab />
                        </div>
                      )}
                      {activeTab === 'sql' && (
                        <div className="h-full overflow-y-auto p-4">
                          <SqlEditorTab />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
